import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Camera, Upload, Plus, Sparkles, CheckCircle2, AlertTriangle, Receipt,
  Building2, FileText, ArrowRight, X, Eye, Edit3, Layers, Zap,
} from 'lucide-react';

/**
 * QUICK CAPTURE — Visual Mockup / Preview Page
 *
 * Pure UI mockup so the user can see and decide between layout options
 * before we build the real feature. Routes to /quick-capture-preview.
 *
 * No backend wiring. All data is hard-coded for the demo.
 */

// ---------- Hard-coded mock data ----------
const MOCK_PROJECTS = [
  { id: '1', name: 'Cedar Rapids Custom Home', address: 'Cedar Rapids, IA', recent: true },
  { id: '2', name: 'Solon Modern Farmhouse', address: 'Solon, IA', recent: false },
  { id: '3', name: 'Iowa City Renovation', address: 'Iowa City, IA', recent: false },
];

const MOCK_PHASES = [
  { id: 'p1', name: 'Lumber - Framing', construction_phase: 'Phase 3: Framing', estimated: 18000, actual: 13500 },
  { id: 'p2', name: 'Concrete - Foundation', construction_phase: 'Phase 2: Site Prep', estimated: 22000, actual: 22000 },
  { id: 'p3', name: 'Electrical Rough-In', construction_phase: 'Phase 4: Rough-In', estimated: 14000, actual: 0 },
];

const COST_TYPES = [
  { id: 'paid', label: 'Paid', desc: 'Already paid in full', color: 'green', icon: CheckCircle2 },
  { id: 'partial', label: 'Partial Payment', desc: 'Some paid, more due', color: 'blue', icon: Layers },
  { id: 'invoice', label: 'Invoice / Bill Due', desc: 'Need to pay this', color: 'amber', icon: FileText },
  { id: 'estimate', label: 'Estimate / Quote', desc: 'Not committed yet', color: 'slate', icon: Edit3 },
];

const MOCK_BULK_QUEUE = [
  { id: 'r1', vendor: 'Home Depot', amount: 847.32, date: '2025-10-14', status: 'new', confidence: 0.94, suggested_phase: 'Lumber - Framing', cost_type: 'paid' },
  { id: 'r2', vendor: 'Menards', amount: 1230.50, date: '2025-10-15', status: 'new', confidence: 0.91, suggested_phase: 'Lumber - Framing', cost_type: 'paid' },
  { id: 'r3', vendor: 'Home Depot', amount: 847.32, date: '2025-10-14', status: 'duplicate', confidence: 1.0, dup_of: 'r1' },
  { id: 'r4', vendor: 'Iowa City Lumber', amount: 4500.00, date: '2025-10-18', status: 'new', confidence: 0.87, suggested_phase: 'Lumber - Framing', cost_type: 'invoice' },
  { id: 'r5', vendor: 'ABC Plumbing', amount: 6800.00, date: '2025-10-20', status: 'new', confidence: 0.62, suggested_phase: 'CREATE NEW', cost_type: 'estimate' },
  { id: 'r6', vendor: 'Menards', amount: 1230.50, date: '2025-10-15', status: 'duplicate', confidence: 1.0, dup_of: 'r2' },
  { id: 'r7', vendor: 'Sherwin-Williams', amount: 412.18, date: '2025-10-22', status: 'review', confidence: 0.41 },
];

// ---------- Tiny presentational helpers ----------
const SectionTitle = ({ children, sub }) => (
  <div className="mb-4">
    <h2 className="text-2xl font-bold text-slate-900">{children}</h2>
    {sub && <p className="text-slate-600 mt-1">{sub}</p>}
  </div>
);

const Pill = ({ color = 'slate', children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>{children}</span>
);

// Simulated phone frame
const PhoneFrame = ({ children, label }) => (
  <div className="flex flex-col items-center">
    <div className="text-xs text-slate-500 mb-2 font-medium">{label}</div>
    <div className="w-[280px] h-[560px] bg-slate-900 rounded-[40px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[32px] overflow-hidden relative">
        {children}
      </div>
    </div>
  </div>
);

// ---------- Question 1 — Entry point placement ----------
const Question1Visual = () => (
  <div className="space-y-6">
    <SectionTitle sub="Where the Quick Capture button lives. Pick option (a) or (b).">
      Question 1 — Entry point placement
    </SectionTitle>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Option A — Floating Action Button */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center justify-between">
            <span className="text-blue-900">Option A — Floating Action Button (FAB)</span>
            <Badge className="bg-blue-600">Mobile-first</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 flex justify-center">
          <PhoneFrame label="Inside any project page">
            {/* Fake page header */}
            <div className="bg-slate-100 px-3 py-2 text-[10px] text-slate-600 border-b">Cedar Rapids Custom Home</div>
            <div className="p-3 space-y-2">
              <div className="bg-slate-100 rounded h-6 w-3/4" />
              <div className="bg-slate-100 rounded h-3 w-1/2" />
              <div className="bg-slate-50 border rounded p-2 mt-3">
                <div className="text-[9px] text-slate-500 mb-1">PHASE 3: FRAMING</div>
                <div className="h-2 bg-emerald-200 rounded w-3/4" />
                <div className="text-[9px] text-slate-600 mt-1">$13.5K / $18K</div>
              </div>
              <div className="bg-slate-50 border rounded p-2">
                <div className="text-[9px] text-slate-500 mb-1">PHASE 4: ROUGH-IN</div>
                <div className="h-2 bg-slate-200 rounded w-1/4" />
                <div className="text-[9px] text-slate-600 mt-1">$0 / $14K</div>
              </div>
            </div>
            {/* The FAB */}
            <button className="absolute bottom-5 right-5 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-xl flex items-center justify-center text-white animate-pulse">
              <Camera className="w-6 h-6" />
            </button>
            <div className="absolute bottom-7 right-24 bg-slate-900 text-white text-[10px] px-2 py-1 rounded shadow-lg">
              Snap or Upload
              <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 border-l-4 border-l-slate-900 border-y-4 border-y-transparent" />
            </div>
          </PhoneFrame>
        </CardContent>
        <div className="bg-blue-50 px-6 py-3 text-sm text-blue-900 border-t">
          <div className="font-semibold mb-1">How it feels</div>
          One thumb tap on the orange camera button — works the same on mobile and desktop. Always visible, never in the way.
        </div>
      </Card>

      {/* Option B — Top button + sidebar */}
      <Card className="border-2 border-purple-200">
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center justify-between">
            <span className="text-purple-900">Option B — Top dashboard button + sidebar</span>
            <Badge className="bg-purple-600">Desktop-prominent</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Fake desktop screenshot */}
          <div className="border rounded-lg overflow-hidden shadow-lg bg-white">
            <div className="bg-slate-900 text-white px-3 py-2 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                <div className="w-2 h-2 rounded-full bg-green-400" />
              </div>
              <div>drawready.com / project / cedar-rapids</div>
              <div />
            </div>
            <div className="flex">
              {/* Sidebar */}
              <div className="w-[110px] bg-slate-50 border-r p-2 space-y-1 text-[10px]">
                <div className="text-slate-500">DrawReady</div>
                <div className="bg-slate-200 rounded px-2 py-1 text-slate-700">Dashboard</div>
                <div className="px-2 py-1 text-slate-500">Phases</div>
                <div className="px-2 py-1 text-slate-500">Contractors</div>
                <div className="bg-amber-100 border border-amber-300 rounded px-2 py-1 text-amber-800 font-bold flex items-center gap-1">
                  <Camera className="w-3 h-3" /> Capture
                </div>
                <div className="px-2 py-1 text-slate-500">Reports</div>
              </div>
              {/* Main content */}
              <div className="flex-1 p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold text-slate-900">Cedar Rapids Custom Home</div>
                  <button className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[10px] px-3 py-1.5 rounded-md shadow flex items-center gap-1 font-semibold">
                    <Camera className="w-3 h-3" /> Quick Capture
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 border rounded p-2">
                    <div className="text-[8px] text-slate-500">DAILY BURN</div>
                    <div className="text-xs font-bold text-red-600">$147/day</div>
                  </div>
                  <div className="bg-slate-50 border rounded p-2">
                    <div className="text-[8px] text-slate-500">DRAW-READY</div>
                    <div className="text-xs font-bold text-emerald-600">82/100</div>
                  </div>
                </div>
                <div className="mt-2 text-[8px] text-slate-500">8-PHASE PROGRESS</div>
                <div className="flex gap-1 mt-1">
                  {[1, 1, 0.7, 0.3, 0, 0, 0, 0].map((p, i) => (
                    <div key={i} className="flex-1 h-3 bg-slate-200 rounded">
                      <div className="h-full bg-emerald-500 rounded" style={{ width: `${p * 100}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <div className="bg-purple-50 px-6 py-3 text-sm text-purple-900 border-t">
          <div className="font-semibold mb-1">How it feels</div>
          A bold gradient button at the top of every project page <em>and</em> a permanent sidebar shortcut. More visible on desktop, more "official" looking.
        </div>
      </Card>
    </div>

    <div className="bg-slate-100 border-l-4 border-amber-500 p-4 rounded">
      <div className="font-semibold text-slate-900">My nudge</div>
      <p className="text-sm text-slate-700 mt-1">
        Pick <strong>A</strong> if you think most users will be on a phone at job sites (probable). Pick <strong>B</strong> if you want desktop power-users to feel it's a primary feature.
        Honestly we can ship <strong>both</strong> — the FAB on mobile, the top button + sidebar on desktop. Same backend, just two presentations. No extra dev cost.
      </p>
    </div>
  </div>
);

// ---------- Question 2 — Cost type chooser ----------
const Question2Visual = () => {
  const [selected, setSelected] = useState('paid');
  return (
    <div className="space-y-4">
      <SectionTitle sub="After AI extracts the receipt, how the user picks Paid / Partial / Invoice / Estimate.">
        Question 2 — Cost type chooser (your answer)
      </SectionTitle>
      <Card className="border-2 border-emerald-200">
        <CardContent className="p-6 space-y-5">
          {/* Mock receipt thumbnail */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-100 border rounded-lg p-4 text-center">
              <div className="bg-white border rounded shadow-inner mx-auto w-32 h-44 flex flex-col items-center justify-center text-[10px] text-slate-500">
                <Receipt className="w-8 h-8 text-slate-400 mb-2" />
                <div className="font-bold text-slate-700">HOME DEPOT</div>
                <div>10/14/2025</div>
                <div className="font-bold text-slate-900 mt-1">$847.32</div>
              </div>
              <div className="text-xs text-slate-500 mt-2">Captured photo</div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-emerald-700">
                <Sparkles className="w-4 h-4" /> <span className="font-semibold">AI extracted</span>
              </div>
              <div><span className="text-slate-500">Vendor:</span> <strong>Home Depot</strong></div>
              <div><span className="text-slate-500">Amount:</span> <strong>$847.32</strong></div>
              <div><span className="text-slate-500">Date:</span> <strong>Oct 14, 2025</strong></div>
              <div><span className="text-slate-500">Suggested phase:</span> <strong className="text-blue-700">Phase 3: Framing → Lumber</strong></div>
              <div><span className="text-slate-500">Suggested type:</span> <Pill color="green">Paid</Pill></div>
            </div>
          </div>

          {/* Cost type chooser */}
          <div>
            <Label className="text-sm font-semibold text-slate-800 mb-2 block">Confirm cost type</Label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {COST_TYPES.map((ct) => {
                const Icon = ct.icon;
                const isActive = selected === ct.id;
                return (
                  <button
                    key={ct.id}
                    onClick={() => setSelected(ct.id)}
                    className={`text-left rounded-lg border-2 p-3 transition-all ${
                      isActive
                        ? `border-${ct.color}-500 bg-${ct.color}-50 shadow-md`
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className={`flex items-center gap-2 mb-1 ${isActive ? `text-${ct.color}-700` : 'text-slate-700'}`}>
                      <Icon className="w-4 h-4" /> <span className="font-semibold text-sm">{ct.label}</span>
                    </div>
                    <p className="text-xs text-slate-500">{ct.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Partial payment expansion */}
          {selected === 'partial' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="text-sm font-semibold text-blue-900">Partial payment details</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Amount paid so far</Label>
                  <Input defaultValue="500.00" />
                </div>
                <div>
                  <Label className="text-xs">Total invoice</Label>
                  <Input defaultValue="847.32" />
                </div>
              </div>
              <div className="text-xs text-blue-700">Balance due: <strong>$347.32</strong></div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              File Receipt <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- Question 3 — Project resolution ----------
const Question3Visual = () => (
  <div className="space-y-4">
    <SectionTitle sub="Global button anywhere in DrawReady → AI guesses project, user can override.">
      Question 3 — Project auto-detection (your answer)
    </SectionTitle>
    <Card className="border-2 border-amber-200">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-amber-700">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-semibold">Looks like this belongs to:</span>
        </div>
        <div className="border-2 border-amber-400 bg-amber-50 rounded-lg p-4 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-amber-700" />
          <div className="flex-1">
            <div className="font-bold text-slate-900">Cedar Rapids Custom Home</div>
            <div className="text-xs text-slate-600">Cedar Rapids, IA · 12 prior receipts from Home Depot</div>
          </div>
          <Pill color="amber">94% match</Pill>
        </div>

        <details>
          <summary className="text-sm text-blue-600 cursor-pointer hover:underline">Wrong project? Pick another</summary>
          <div className="mt-3 space-y-2">
            {MOCK_PROJECTS.map((p) => (
              <button key={p.id} className="w-full text-left border rounded-lg p-3 hover:bg-slate-50 flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-500" />
                <div className="flex-1">
                  <div className="font-medium text-slate-800 text-sm">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.address}</div>
                </div>
                {p.recent && <Pill color="blue">Recent</Pill>}
              </button>
            ))}
          </div>
        </details>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline">Skip</Button>
          <Button className="bg-amber-600 hover:bg-amber-700">Confirm Project</Button>
        </div>
      </CardContent>
    </Card>
    <div className="text-sm text-slate-600 italic px-2">
      ⚡ When you're already inside a project page → this step is skipped entirely.
    </div>
  </div>
);

// ---------- Question 4 — Add to existing OR create new ----------
const Question4Visual = () => {
  const [mode, setMode] = useState('existing');
  return (
    <div className="space-y-4">
      <SectionTitle sub="When AI suggests filing a receipt, user picks: add to existing phase OR create new.">
        Question 4 — Phase resolution (your answer)
      </SectionTitle>
      <Card className="border-2 border-blue-200">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMode('existing')}
              className={`rounded-lg border-2 p-4 text-left transition-all ${
                mode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <Layers className="w-4 h-4" /> Add to existing phase
              </div>
              <div className="text-xs text-slate-500 mt-1">DrawReady picks the best match — you can override.</div>
            </button>
            <button
              onClick={() => setMode('new')}
              className={`rounded-lg border-2 p-4 text-left transition-all ${
                mode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <Plus className="w-4 h-4" /> Create new phase
              </div>
              <div className="text-xs text-slate-500 mt-1">No matching phase exists — make one on the fly.</div>
            </button>
          </div>

          {mode === 'existing' && (
            <div className="space-y-2">
              <Label className="text-xs text-slate-600">AI suggested matches (highest confidence first)</Label>
              {MOCK_PHASES.map((ph, i) => (
                <button key={ph.id} className={`w-full text-left border rounded-lg p-3 hover:bg-blue-50 ${i === 0 ? 'border-blue-400 bg-blue-50/40' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-800 text-sm">{ph.name}</div>
                      <div className="text-xs text-slate-500">{ph.construction_phase}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Actual</div>
                      <div className="text-sm font-bold text-slate-900">${ph.actual.toLocaleString()}</div>
                    </div>
                    {i === 0 && <Pill color="blue">Best match</Pill>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {mode === 'new' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
              <div className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> AI pre-filled new phase
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Phase name</Label>
                  <Input defaultValue="Plumbing Rough-In - ABC Plumbing" />
                </div>
                <div>
                  <Label className="text-xs">Construction phase</Label>
                  <Input defaultValue="Phase 4: Rough-In" />
                </div>
                <div>
                  <Label className="text-xs">Estimated cost</Label>
                  <Input defaultValue="6,800.00" />
                </div>
                <div>
                  <Label className="text-xs">Bank category</Label>
                  <Input defaultValue="403 Plumbing" />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline">Back</Button>
            <Button className="bg-blue-600 hover:bg-blue-700">File Receipt <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- Question 5 — Bulk upload + duplicate detection ----------
const Question5Visual = () => {
  const newCount = MOCK_BULK_QUEUE.filter((r) => r.status === 'new').length;
  const dupCount = MOCK_BULK_QUEUE.filter((r) => r.status === 'duplicate').length;
  const reviewCount = MOCK_BULK_QUEUE.filter((r) => r.status === 'review').length;
  const totalNew = MOCK_BULK_QUEUE.filter((r) => r.status === 'new').reduce((s, r) => s + r.amount, 0);

  return (
    <div className="space-y-4">
      <SectionTitle sub="Drag a stack of receipts → AI extracts, detects duplicates, then ONE confirmation screen per new receipt.">
        Question 5 — Bulk upload + duplicate detection (your answer)
      </SectionTitle>

      {/* Upload zone */}
      <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50">
        <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
        <div className="text-slate-700 font-semibold">Drop receipts here</div>
        <div className="text-xs text-slate-500">JPG, PNG, PDF, HEIC · up to 50 at once</div>
      </div>

      {/* Summary screen */}
      <Card className="border-2 border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Bulk upload summary</span>
            <Pill color="blue">7 receipts processed</Pill>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="text-xs text-emerald-700 font-semibold">NEW</div>
              <div className="text-2xl font-bold text-emerald-900">{newCount}</div>
              <div className="text-xs text-emerald-700">${totalNew.toLocaleString()}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-xs text-amber-700 font-semibold">DUPLICATES</div>
              <div className="text-2xl font-bold text-amber-900">{dupCount}</div>
              <div className="text-xs text-amber-700">Will be skipped</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-700 font-semibold">NEEDS REVIEW</div>
              <div className="text-2xl font-bold text-slate-900">{reviewCount}</div>
              <div className="text-xs text-slate-700">Low AI confidence</div>
            </div>
          </div>

          {/* Receipt list */}
          <div className="border rounded-lg divide-y">
            {MOCK_BULK_QUEUE.map((r, idx) => (
              <div key={r.id} className="px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                  <Receipt className={`w-4 h-4 ${
                    r.status === 'duplicate' ? 'text-amber-600' :
                    r.status === 'review' ? 'text-slate-500' :
                    'text-emerald-600'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{r.vendor}</div>
                  <div className="text-xs text-slate-500">${r.amount.toFixed(2)} · {r.date}</div>
                </div>
                <div className="hidden md:block flex-1 min-w-0 text-right text-xs text-slate-500">
                  {r.status === 'new' && r.suggested_phase === 'CREATE NEW' && (
                    <Pill color="emerald">+ New phase</Pill>
                  )}
                  {r.status === 'new' && r.suggested_phase !== 'CREATE NEW' && (
                    <span>→ {r.suggested_phase}</span>
                  )}
                  {r.status === 'duplicate' && (
                    <span className="text-amber-700">Duplicate of #{r.dup_of?.replace('r','')}</span>
                  )}
                  {r.status === 'review' && (
                    <span className="text-slate-500">Confidence {Math.round((r.confidence || 0) * 100)}%</span>
                  )}
                </div>
                {r.status === 'new' && (
                  <Pill color={r.cost_type === 'paid' ? 'green' : r.cost_type === 'invoice' ? 'amber' : r.cost_type === 'estimate' ? 'slate' : 'blue'}>
                    {COST_TYPES.find((c) => c.id === r.cost_type)?.label}
                  </Pill>
                )}
                {r.status === 'duplicate' && <Pill color="amber">Skip</Pill>}
                {r.status === 'review' && <Pill color="slate">Review</Pill>}
              </div>
            ))}
          </div>

          {/* Action footer */}
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-slate-600">
              We'll show one confirmation card for each <strong>new</strong> receipt — duplicates auto-skip.
            </div>
            <div className="flex gap-2">
              <Button variant="outline">Review duplicates</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Zap className="w-4 h-4 mr-1" /> Start filing {newCount} receipts
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Duplicate detail card */}
      <Card className="border-2 border-amber-200">
        <CardHeader className="bg-amber-50">
          <CardTitle className="text-amber-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> What a duplicate alert looks like (single upload)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-amber-900">Possible duplicate</div>
              <div className="text-sm text-amber-800 mt-1">
                This looks like <strong>Home Depot — $847.32 on Oct 14</strong>, already filed under <strong>Phase 3: Framing → Lumber</strong>.
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="bg-white"><X className="w-3 h-3 mr-1" /> Skip — already filed</Button>
                <Button size="sm" variant="outline" className="bg-white"><Eye className="w-3 h-3 mr-1" /> View existing</Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700">Add anyway (it's different)</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- "How we'll show it everywhere" — Responsive layout + announcement layers ----------
const ResponsiveLayoutVisual = () => (
  <div className="space-y-6">
    <SectionTitle sub="Hybrid layout — flagship presence on desktop, thumb-friendly on mobile, same backend.">
      Hybrid Layout — Desktop + Mobile
    </SectionTitle>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Desktop mock */}
      <div className="lg:col-span-2">
        <div className="text-xs text-slate-500 mb-2 font-medium">Desktop (&gt; 1024px) — Quick Capture has 3 entry points</div>
        <div className="border rounded-xl overflow-hidden shadow-xl bg-white relative">
          <div className="bg-slate-900 text-white px-3 py-2 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-400" />
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <div className="w-2 h-2 rounded-full bg-green-400" />
            </div>
            <div>drawready.com / project / cedar-rapids</div>
            <div />
          </div>
          <div className="flex h-[420px]">
            {/* Sidebar */}
            <div className="w-[140px] bg-slate-50 border-r p-3 space-y-1 text-[11px]">
              <div className="text-slate-500 mb-2 font-bold">DrawReady</div>
              <div className="bg-slate-200 rounded px-2 py-1.5 text-slate-700">Dashboard</div>
              <div className="px-2 py-1.5 text-slate-500">Phases</div>
              <div className="px-2 py-1.5 text-slate-500">Contractors</div>
              {/* Sidebar entry point with NEW badge */}
              <div className="relative">
                <div className="bg-amber-100 border border-amber-300 rounded px-2 py-1.5 text-amber-800 font-bold flex items-center gap-1.5">
                  <Camera className="w-3 h-3" /> Capture
                </div>
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">NEW</span>
              </div>
              <div className="px-2 py-1.5 text-slate-500">Reports</div>
              <div className="px-2 py-1.5 text-slate-500">Settings</div>
            </div>
            {/* Main */}
            <div className="flex-1 p-4 relative bg-slate-50">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-bold text-slate-900">Cedar Rapids Custom Home</div>
                {/* Top-bar Quick Capture button with NEW badge */}
                <div className="relative">
                  <button className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs px-3 py-2 rounded-md shadow-md flex items-center gap-1.5 font-semibold">
                    <Camera className="w-3.5 h-3.5" /> Quick Capture
                    <span className="text-[9px] opacity-80 ml-1 border border-white/40 rounded px-1">⌘K</span>
                  </button>
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">NEW</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-white border rounded-lg p-2.5">
                  <div className="text-[9px] text-slate-500">DAILY BURN</div>
                  <div className="text-sm font-bold text-red-600">$147/day</div>
                </div>
                <div className="bg-white border rounded-lg p-2.5">
                  <div className="text-[9px] text-slate-500">DRAW-READY</div>
                  <div className="text-sm font-bold text-emerald-600">82/100</div>
                </div>
                <div className="bg-white border rounded-lg p-2.5">
                  <div className="text-[9px] text-slate-500">RECEIPTS</div>
                  <div className="text-sm font-bold text-slate-900">47 filed</div>
                </div>
              </div>
              <div className="text-[9px] text-slate-500">8-PHASE PROGRESS</div>
              <div className="flex gap-1 mt-1">
                {[1, 1, 0.7, 0.3, 0, 0, 0, 0].map((p, i) => (
                  <div key={i} className="flex-1 h-3 bg-slate-200 rounded">
                    <div className="h-full bg-emerald-500 rounded" style={{ width: `${p * 100}%` }} />
                  </div>
                ))}
              </div>

              {/* FAB on desktop too — semi-transparent until hover */}
              <div className="absolute bottom-4 right-4">
                <button className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-2xl flex items-center justify-center text-white hover:scale-105 transition-transform">
                  <Camera className="w-5 h-5" />
                </button>
                {/* Tooltip on hover (always shown in mock) */}
                <div className="absolute bottom-14 right-0 bg-slate-900 text-white text-[10px] px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
                  <div className="font-bold">Quick Capture</div>
                  <div className="text-slate-300">Snap a receipt — AI files it</div>
                  <div className="text-amber-300 mt-0.5">⌘K</div>
                  <div className="absolute right-4 -bottom-1 w-2 h-2 bg-slate-900 rotate-45" />
                </div>
              </div>

              {/* Annotations */}
              <div className="absolute top-12 right-2 text-[9px] text-amber-700 font-bold">① Top-bar button</div>
              <div className="absolute top-1 left-32 text-[9px] text-amber-700 font-bold">② Sidebar shortcut</div>
              <div className="absolute bottom-20 right-20 text-[9px] text-amber-700 font-bold">③ FAB (always reachable)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile mock */}
      <div>
        <div className="text-xs text-slate-500 mb-2 font-medium">Mobile (&lt; 640px) — FAB only</div>
        <PhoneFrame label="">
          <div className="bg-white px-3 py-2 text-[10px] text-slate-600 border-b flex items-center justify-between">
            <span>☰</span>
            <span className="font-bold">Cedar Rapids</span>
            <span>⋮</span>
          </div>
          <div className="p-3 space-y-2">
            <div className="bg-slate-50 border rounded-lg p-2">
              <div className="text-[9px] text-slate-500">DAILY BURN</div>
              <div className="text-sm font-bold text-red-600">$147/day</div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-2">
              <div className="text-[9px] text-slate-500">DRAW-READY SCORE</div>
              <div className="text-sm font-bold text-emerald-600">82/100</div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-2 mt-3">
              <div className="text-[9px] text-slate-500 mb-1">PHASE 3: FRAMING</div>
              <div className="h-2 bg-emerald-200 rounded w-3/4" />
              <div className="text-[9px] text-slate-600 mt-1">$13.5K / $18K</div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-2">
              <div className="text-[9px] text-slate-500 mb-1">PHASE 4: ROUGH-IN</div>
              <div className="h-2 bg-slate-200 rounded w-1/4" />
              <div className="text-[9px] text-slate-600 mt-1">$0 / $14K</div>
            </div>
          </div>
          {/* FAB */}
          <button className="absolute bottom-5 right-5 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-xl flex items-center justify-center text-white">
            <Camera className="w-6 h-6" />
          </button>
          <span className="absolute bottom-[68px] right-[68px] bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">NEW</span>
        </PhoneFrame>
      </div>
    </div>

    {/* Behavior table */}
    <Card className="border-2 border-slate-200">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left p-3">Element</th>
              <th className="text-left p-3">📱 Mobile</th>
              <th className="text-left p-3">📱 Tablet</th>
              <th className="text-left p-3">💻 Desktop</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr><td className="p-3 font-semibold">Quick Capture trigger</td><td className="p-3">FAB only</td><td className="p-3">FAB + top button</td><td className="p-3">Top button + sidebar + FAB</td></tr>
            <tr><td className="p-3 font-semibold">Camera vs Upload</td><td className="p-3">Camera default</td><td className="p-3">Both side-by-side</td><td className="p-3">Upload primary, webcam secondary</td></tr>
            <tr><td className="p-3 font-semibold">AI confirm card</td><td className="p-3">Full-screen modal</td><td className="p-3">Centered ~80%</td><td className="p-3">Centered ~600px</td></tr>
            <tr><td className="p-3 font-semibold">Bulk drag-drop</td><td className="p-3">"Tap to add" multi-shot</td><td className="p-3">Drop zone + add files</td><td className="p-3">Full drag-drop with hover</td></tr>
            <tr><td className="p-3 font-semibold">Cmd+K shortcut</td><td className="p-3">—</td><td className="p-3">If keyboard attached</td><td className="p-3">Always</td></tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  </div>
);

// ---------- Announcement layers visual ----------
const AnnouncementLayersVisual = () => (
  <div className="space-y-6">
    <SectionTitle sub="5 layered touchpoints so existing users actually discover Quick Capture — without nagging them.">
      How we'll tell users about it
    </SectionTitle>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Layer 1 — Spotlight */}
      <Card className="border-2 border-amber-200">
        <CardHeader className="bg-amber-50">
          <CardTitle className="text-amber-900 text-base">① First-time spotlight (one-time tour step)</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-slate-900 rounded-lg p-6 relative h-[260px]">
            {/* Dim overlay */}
            <div className="absolute inset-0 bg-black/70 rounded-lg" />
            <div className="relative z-10 flex items-end justify-end h-full gap-4">
              {/* Popover */}
              <div className="bg-white rounded-xl shadow-2xl p-4 w-56">
                <div className="flex items-center gap-1 text-amber-600 text-xs font-bold mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> NEW FEATURE
                </div>
                <div className="font-bold text-slate-900 text-sm">Quick Capture</div>
                <p className="text-xs text-slate-600 mt-1">
                  Snap any receipt or invoice. AI files it automatically — across all your projects.
                </p>
                <div className="flex gap-2 mt-3">
                  <button className="flex-1 bg-amber-500 text-white text-xs py-1.5 rounded font-semibold">Try it now</button>
                  <button className="text-xs text-slate-500 px-2">Skip</button>
                </div>
              </div>
              {/* Highlighted FAB */}
              <div className="relative">
                <div className="absolute -inset-3 bg-amber-400/30 rounded-full animate-ping" />
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-2xl flex items-center justify-center text-white relative">
                  <Camera className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Uses our existing 17-step Welcome Tour pattern. One step. Dismissible. Tracked in localStorage.</p>
        </CardContent>
      </Card>

      {/* Layer 2 — Hover tooltip */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="text-blue-900 text-base">② Permanent hover tooltip</CardTitle>
        </CardHeader>
        <CardContent className="p-6 flex items-center justify-center h-[260px] bg-slate-50 rounded-b-xl">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-2xl flex items-center justify-center text-white">
              <Camera className="w-7 h-7" />
            </div>
            <div className="absolute bottom-20 right-0 bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
              <div className="font-bold">Quick Capture</div>
              <div className="text-slate-300 text-[11px]">Snap a receipt — AI files it</div>
              <div className="text-amber-300 text-[10px] mt-1">⌘K</div>
              <div className="absolute right-4 -bottom-1 w-2 h-2 bg-slate-900 rotate-45" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Layer 3 — NEW badge */}
      <Card className="border-2 border-rose-200">
        <CardHeader className="bg-rose-50">
          <CardTitle className="text-rose-900 text-base">③ "NEW" badge (auto-fades after 30 days)</CardTitle>
        </CardHeader>
        <CardContent className="p-6 bg-slate-50 rounded-b-xl">
          <div className="flex flex-col gap-4 items-start">
            <div className="relative">
              <button className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm px-4 py-2.5 rounded-md shadow flex items-center gap-2 font-semibold">
                <Camera className="w-4 h-4" /> Quick Capture
              </button>
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">NEW</span>
            </div>
            <div className="text-xs text-slate-600">
              Disappears after <strong>30 days</strong> from user's first login OR after their <strong>first successful capture</strong>.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Layer 4 — Empty-state nudge */}
      <Card className="border-2 border-emerald-200">
        <CardHeader className="bg-emerald-50">
          <CardTitle className="text-emerald-900 text-base">④ Empty-state nudge (passive discovery)</CardTitle>
        </CardHeader>
        <CardContent className="p-6 bg-slate-50 rounded-b-xl">
          <div className="bg-white border-2 border-dashed border-emerald-300 rounded-lg p-4 flex items-center gap-3">
            <Receipt className="w-6 h-6 text-emerald-600" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">Lumber - Framing</div>
              <div className="text-xs text-slate-600">Marked paid: $4,200 · 0 receipts attached</div>
              <div className="text-xs text-amber-700 mt-1 font-medium">💡 Got a receipt for this? Snap it now →</div>
            </div>
            <button className="bg-amber-500 hover:bg-amber-600 text-white text-xs px-3 py-1.5 rounded-md font-semibold flex items-center gap-1">
              <Camera className="w-3.5 h-3.5" /> Capture
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3">Shown on any phase with `actual_cost &gt; 0` and zero receipts. Catches the user when they need it.</p>
        </CardContent>
      </Card>

      {/* Layer 5 — Email */}
      <Card className="border-2 border-purple-200 md:col-span-2">
        <CardHeader className="bg-purple-50">
          <CardTitle className="text-purple-900 text-base">⑤ One-time launch email</CardTitle>
        </CardHeader>
        <CardContent className="p-6 bg-slate-50 rounded-b-xl">
          <div className="bg-white border rounded-lg shadow-sm max-w-xl mx-auto">
            <div className="border-b p-3 text-xs text-slate-500 space-y-0.5">
              <div><strong>From:</strong> Mod &lt;mod@drawready.com&gt;</div>
              <div><strong>Subject:</strong> Drop a stack of receipts. We'll do the rest.</div>
            </div>
            <div className="p-4 text-sm text-slate-700 space-y-2">
              <p>Hey —</p>
              <p>Quick update: you can now drop a stack of receipts (or even a single one) and DrawReady files them automatically. We'll match them to the right phase, catch any duplicates, and update your numbers.</p>
              <p>Snap or upload from anywhere — phone, desktop, mid-Home-Depot-run.</p>
              <p>👉 <a className="text-amber-600 underline" href="#">Try it on your project</a></p>
              <p>— Mod</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">Goes out once via SendGrid when we ship. No follow-ups.</p>
        </CardContent>
      </Card>
    </div>
  </div>
);

// ---------- Page ----------
export default function QuickCapturePreview() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-12">
        {/* Hero */}
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center gap-3 mb-2">
            <Camera className="w-8 h-8" />
            <Badge className="bg-white text-orange-700 hover:bg-white">PREVIEW · NOT YET BUILT</Badge>
          </div>
          <h1 className="text-4xl font-bold">Quick Capture — Visual Spec</h1>
          <p className="text-lg text-white/90 mt-2 max-w-2xl">
            Drop a receipt or invoice from anywhere in DrawReady. AI extracts every detail, detects duplicates,
            and files it under the right phase — across all your projects.
          </p>
          <div className="flex flex-wrap gap-2 mt-4 text-sm">
            <span className="bg-white/20 backdrop-blur rounded-full px-3 py-1">📸 Snap or upload</span>
            <span className="bg-white/20 backdrop-blur rounded-full px-3 py-1">🤖 AI extract + categorize</span>
            <span className="bg-white/20 backdrop-blur rounded-full px-3 py-1">🔍 Duplicate detection</span>
            <span className="bg-white/20 backdrop-blur rounded-full px-3 py-1">📁 Auto-file to phase</span>
          </div>
        </div>

        <Question1Visual />
        <ResponsiveLayoutVisual />
        <AnnouncementLayersVisual />
        <Question2Visual />
        <Question3Visual />
        <Question4Visual />
        <Question5Visual />

        <div className="bg-slate-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold">Final 2 questions before I lock the spec</h2>
          <div className="text-slate-300 mt-3 space-y-2 text-left max-w-xl mx-auto">
            <div>① <strong>Cmd/Ctrl + K</strong> shortcut to open Quick Capture from anywhere — include in v1?</div>
            <div>② <strong>Launch email</strong> to existing users — auto-send when we ship, or you approve copy first?</div>
          </div>
        </div>
      </div>
    </div>
  );
}
